/**
 * Root route.
 * - Native: UNCHANGED — session-loading skeleton → Redirect to /login or
 *   /(tabs)/home.
 * - Web: usebero.com marketing homepage (desktop + mobile), port of
 *   design/redesign_v3/"Marketing Homepage.dc.html". Never redirects a
 *   signed-in web visitor away from the marketing page — the header just
 *   swaps "Sign in" for "Open app →".
 *
 * FLAGGED DEVIATIONS (rules override pixel fidelity — see build brief):
 * - "amber once per screen": the design uses --accent repeatedly (every
 *   bullet dot, every mock-row tag, the manifesto stat numbers). This page
 *   uses accent exactly once — the figure inside the hero ArchSkyline.
 *   Every other bullet/tag below is recolored to primary/success/tertiary.
 * - "zero hex / theme tokens": the manifesto's solid dark-green panel (the
 *   design hard-codes white/amber ink on it because css var(--primary-900)
 *   itself flips light/dark and would go low-contrast) is rebuilt from
 *   `colors.primary900` + scheme-aware ink (light → textOnDark, dark →
 *   textPrimary/textSecondary) so it holds up in both themes without hex.
 * - Footer/legal links open https://usebero.com/privacy via Linking — no
 *   in-app /privacy route exists yet (same pattern app/login.tsx already
 *   uses for its Terms/Privacy links).
 * - The design's decorative read-only "email" input next to the demo CTA
 *   isn't reproduced — a field that looks interactive but does nothing is
 *   worse UX than a single clear button; "Request a demo" is one mailto: CTA.
 */
import { Redirect, router } from 'expo-router';
import React from 'react';
import { Linking, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { ArchSkyline } from '@/components/brand';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { homeFor } from '@/lib/homeFor';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, Screen, Skeleton } from '@/ui';

const WIDE = 1024;
const DEMO_MAILTO = 'mailto:hello@usebero.com?subject=Bero%20demo%20request';
const LEGAL_URL = 'https://usebero.com/privacy';

/* ---------------- native: unchanged behaviour ---------------- */

function NativeGate() {
  const { user, loading } = useSession();
  if (loading) {
    return (
      <Screen scroll={false} padded style={{ justifyContent: 'center', gap: 12 }}>
        <Skeleton h={22} w="60%" />
        <Skeleton h={16} w="90%" />
        <Skeleton h={16} w="80%" />
      </Screen>
    );
  }
  if (!user) return <Redirect href="/login" />;
  return <Redirect href={homeFor(user) as '/(tabs)/home'} />;
}

/* ---------------- small building blocks ---------------- */

/** Section-header glyph — small outlined arch, per the brand's "arch motif" rule. */
function ArchGlyph({ color, size = 15 }: { color: string; size?: number }) {
  return (
    <View style={{
      width: size, height: size * 0.62, borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
      borderWidth: 2, borderBottomWidth: 0, borderColor: color,
    }} />
  );
}

function Kicker({ label }: { label: string }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <ArchGlyph color={colors.primary500} />
      <AppText variant="label" style={{ color: colors.primary500, letterSpacing: 1 }}>{label}</AppText>
    </View>
  );
}

function NavLink({ label, onPress }: { label: string; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="link"
      onPress={onPress}
      hitSlop={8}
      style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({ opacity: hovered || pressed ? 0.65 : 1 })}
    >
      <AppText variant="bodyMd" weight="600" tone="secondary">{label}</AppText>
    </Pressable>
  );
}

type Tone = 'primary' | 'success' | 'tertiary';

function tagColors(colors: ReturnType<typeof useTheme>['colors'], tone: Tone) {
  if (tone === 'primary') return { bg: colors.primary100, fg: colors.primary700 };
  if (tone === 'success') return { bg: colors.success50, fg: colors.success700 };
  return { bg: colors.canvas2, fg: colors.textTertiary };
}

type Vars = Record<string, string | number>;
// Mock-row cells: `a` (primary line) is fake DATA kept literal per the build
// brief (names/amounts/notice titles/dates); `aKey` overrides it only where the
// line is generic UI copy ('Collections {pct}'). `b`/`tag` translate via keys,
// with a literal fallback for pure-data cells (dates '—', percentages).
type MockRow = {
  a: string; aKey?: DictKey; aVars?: Vars;
  bKey?: DictKey; bText?: string; bVars?: Vars;
  tagKey?: DictKey; tagText?: string;
  tone: Tone;
};
type Feature = {
  key: string; kickerKey: DictKey; reverse: boolean; titleKey: DictKey; bodyKey: DictKey;
  pointsKeys: DictKey[]; frameBg: 'primary50' | 'canvas1' | 'primary100'; mockTitleKey: DictKey; mockRows: MockRow[];
};

const FEATURES: Feature[] = [
  {
    key: 'gate', kickerKey: 'mkt_nav_gate', reverse: false,
    titleKey: 'mkt_f_gate_title',
    bodyKey: 'mkt_f_gate_body',
    pointsKeys: ['mkt_f_gate_p1', 'mkt_f_gate_p2', 'mkt_f_gate_p3'],
    frameBg: 'primary50', mockTitleKey: 'mkt_f_gate_mock',
    mockRows: [
      { a: 'Rajesh Kumar', bKey: 'mkt_m_delivery_waiting', tagKey: 'mkt_allow', tone: 'primary' },
      { a: 'Deepak Nair', bKey: 'mkt_m_guest_pass', bVars: { code: 'GP-4482' }, tagKey: 'mkt_valid', tone: 'success' },
      { a: 'Sunita Devi', bKey: 'mkt_m_housekeeping', tagText: '—', tone: 'tertiary' },
    ],
  },
  {
    key: 'community', kickerKey: 'mkt_nav_community', reverse: true,
    titleKey: 'mkt_f_comm_title',
    bodyKey: 'mkt_f_comm_body',
    pointsKeys: ['mkt_f_comm_p1', 'mkt_f_comm_p2', 'mkt_f_comm_p3'],
    frameBg: 'canvas1', mockTitleKey: 'mkt_nav_community',
    mockRows: [
      { a: 'Water shutdown Sunday', bKey: 'mkt_m_urgent_notice', tagText: '182/240', tone: 'tertiary' },
      { a: 'Replace gym treadmills?', bKey: 'mkt_m_poll_voted', bVars: { n: 148 }, tagText: '62%', tone: 'primary' },
      { a: 'Monsoon Potluck', bText: 'Sat 12 Jul', tagKey: 'mkt_rsvp', tone: 'primary' },
    ],
  },
  {
    key: 'payments', kickerKey: 'mkt_nav_payments', reverse: false,
    titleKey: 'mkt_f_pay_title',
    bodyKey: 'mkt_f_pay_body',
    pointsKeys: ['mkt_f_pay_p1', 'mkt_f_pay_p2', 'mkt_f_pay_p3'],
    frameBg: 'primary100', mockTitleKey: 'mkt_f_pay_mock',
    mockRows: [
      { a: '₹4,250 due 10 Jul', bKey: 'mkt_m_maint_sinking', tagKey: 'mkt_pay', tone: 'primary' },
      { a: '₹4,250 · June', bKey: 'mkt_m_paid_via', bVars: { date: '6 Jun' }, tagKey: 'mkt_paid', tone: 'success' },
      { a: '', aKey: 'mkt_m_collections', aVars: { pct: '77%' }, bKey: 'mkt_m_of_flats', bVars: { a: 185, b: 240 }, tagKey: 'mkt_live', tone: 'tertiary' },
    ],
  },
];

const PRICE_POINTS: DictKey[] = ['mkt_pp1', 'mkt_pp2', 'mkt_pp3', 'mkt_pp4'];

/* ---------------- sections ---------------- */

type NavTargets = { gate: () => void; community: () => void; payments: () => void; pricing: () => void };

function NavHeader({ wide, signedIn, navTo }: { wide: boolean; signedIn: boolean; navTo: NavTargets }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center', gap: space.s6,
      paddingHorizontal: wide ? 64 : space.s4, paddingVertical: wide ? space.s5 : space.s4,
      borderBottomWidth: 0.5, borderBottomColor: colors.borderSoft,
    }}>
      <BrandLockup markHeight={wide ? 28 : 24} />
      {wide ? (
        <>
          <NavLink label={t('mkt_nav_gate')} onPress={navTo.gate} />
          <NavLink label={t('mkt_nav_community')} onPress={navTo.community} />
          <NavLink label={t('mkt_nav_payments')} onPress={navTo.payments} />
          <NavLink label={t('mkt_nav_pricing')} onPress={navTo.pricing} />
        </>
      ) : null}
      <View style={{ flex: 1 }} />
      {wide && !signedIn ? <NavLink label={t('sign_in')} onPress={() => router.push('/login')} /> : null}
      <Button
        title={signedIn ? t('mkt_open_app') : wide ? t('mkt_request_demo') : t('sign_in')}
        onPress={() => {
          if (signedIn) router.push('/(tabs)/home');
          else if (wide) void Linking.openURL(DEMO_MAILTO);
          else router.push('/login');
        }}
      />
    </View>
  );
}

function Hero({ wide, onSeeHowItWorks }: { wide: boolean; onSeeHowItWorks: () => void }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{ paddingHorizontal: wide ? 64 : space.s5, paddingTop: wide ? 88 : 48, alignItems: 'center' }}>
      <AppText
        tone="primary"
        style={{
          fontFamily: 'Sora_800ExtraBold', textAlign: 'center',
          fontSize: wide ? 54 : 30, lineHeight: wide ? 62 : 36, letterSpacing: -0.6,
          maxWidth: wide ? 780 : undefined,
        }}
      >
        {t('mkt_hero_title')}
      </AppText>
      <AppText
        variant={wide ? 'bodyLg' : 'bodyMd'}
        tone="secondary"
        style={{ textAlign: 'center', marginTop: space.s5, maxWidth: wide ? 600 : 420 }}
      >
        {t('mkt_hero_sub')}
      </AppText>
      <View style={{ flexDirection: 'row', gap: space.s3, marginTop: space.s6, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button title={t('mkt_request_demo')} size="lg" onPress={() => void Linking.openURL(DEMO_MAILTO)} />
        <Button title={t('mkt_see_how')} kind="secondary" size="lg" onPress={onSeeHowItWorks} />
      </View>
      <View style={{ marginTop: wide ? 56 : 32, width: '100%', alignItems: 'center' }}>
        <ArchSkyline height={wide ? 260 : 150} width={Math.min(wide ? 1100 : 340, 1100)} bare />
      </View>
      <View style={{ height: 0.5, backgroundColor: colors.borderSoft, width: '100%', marginTop: wide ? 40 : 24 }} />
    </View>
  );
}

function MockCard({ feature, wide }: { feature: Feature; wide: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  const frameBg = {
    primary50: colors.primary50, canvas1: colors.canvas1, primary100: colors.primary100,
  }[feature.frameBg];
  const w = wide ? 420 : '100%' as const;
  const cardW = wide ? 260 : 280;
  return (
    <View
      style={{
        width: w, height: wide ? 340 : 260,
        borderTopLeftRadius: wide ? 210 : 140, borderTopRightRadius: wide ? 210 : 140,
        borderBottomLeftRadius: 18, borderBottomRightRadius: 18,
        backgroundColor: frameBg, alignItems: 'center', justifyContent: 'flex-end',
        overflow: 'hidden', borderWidth: 0.5, borderColor: colors.borderSoft,
      }}
    >
      <View style={{
        width: cardW, backgroundColor: colors.canvas0, borderWidth: 0.5, borderColor: colors.border,
        borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: space.s4, gap: space.s2,
      }}>
        <AppText variant="bodyMd" weight="700">{t(feature.mockTitleKey)}</AppText>
        {feature.mockRows.map((row, i) => {
          const tc = tagColors(colors, row.tone);
          const aStr = row.aKey ? t(row.aKey, row.aVars) : row.a;
          const bStr = row.bKey ? t(row.bKey, row.bVars) : (row.bText ?? '');
          const tagStr = row.tagKey ? t(row.tagKey) : (row.tagText ?? '');
          return (
            <View
              key={i}
              style={{
                flexDirection: 'row', alignItems: 'center', gap: space.s2,
                borderWidth: 0.5, borderColor: colors.borderSoft, borderRadius: 11, padding: space.s2,
              }}
            >
              <View style={{
                width: 22, height: 26, flexShrink: 0, backgroundColor: tc.bg,
                borderTopLeftRadius: 12, borderTopRightRadius: 12,
                borderBottomLeftRadius: 3, borderBottomRightRadius: 3,
              }} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="bodySm" weight="600" numberOfLines={1}>{aStr}</AppText>
                <AppText variant="bodySm" tone="tertiary" numberOfLines={1} style={{ fontSize: 10.5 }}>{bStr}</AppText>
              </View>
              <AppText variant="bodySm" weight="700" style={{ color: tc.fg, fontSize: 10.5 }}>{tagStr}</AppText>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function FeatureBlock({ feature, wide }: { feature: Feature; wide: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  const text = (
    <View style={{ flex: 1, gap: space.s2 }}>
      <Kicker label={t(feature.kickerKey)} />
      <AppText
        tone="primary"
        style={{ fontFamily: 'Sora_800ExtraBold', fontSize: wide ? 30 : 21, lineHeight: wide ? 38 : 27, letterSpacing: -0.3, marginTop: 4 }}
      >
        {t(feature.titleKey)}
      </AppText>
      <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 6, lineHeight: 22 }}>{t(feature.bodyKey)}</AppText>
      <View style={{ gap: 8, marginTop: 8 }}>
        {feature.pointsKeys.map((pk) => (
          <View key={pk} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary500 }} />
            <AppText variant="bodySm" weight="500">{t(pk)}</AppText>
          </View>
        ))}
      </View>
    </View>
  );
  return (
    <View style={{
      flexDirection: wide ? (feature.reverse ? 'row-reverse' : 'row') : 'column',
      alignItems: 'center', gap: wide ? 56 : 28,
    }}>
      {text}
      <MockCard feature={feature} wide={wide} />
    </View>
  );
}

function Manifesto({ wide }: { wide: boolean }) {
  const { colors, scheme } = useTheme();
  const { t } = useT();
  const bg = colors.primary900;
  const fg = scheme === 'dark' ? colors.textPrimary : colors.textOnDark;
  const fgMuted = scheme === 'dark' ? colors.textSecondary : colors.primary100;
  const stats: Array<[string, DictKey]> = [['0', 'mkt_stat_ads'], ['0', 'mkt_stat_brokers'], ['100%', 'mkt_stat_funded']];
  return (
    <View style={{ backgroundColor: bg, paddingVertical: wide ? 88 : 48, paddingHorizontal: wide ? 64 : space.s5, alignItems: 'center' }}>
      <ArchGlyph color={fg} size={30} />
      <AppText
        style={{
          fontFamily: 'Sora_800ExtraBold', color: fg, textAlign: 'center',
          fontSize: wide ? 36 : 23, lineHeight: wide ? 44 : 29, letterSpacing: -0.3,
          marginTop: space.s5, maxWidth: 700,
        }}
      >
        {t('mkt_manifesto_title')}
      </AppText>
      <AppText style={{ color: fgMuted, textAlign: 'center', marginTop: space.s4, maxWidth: 560, fontSize: 15, lineHeight: 24 }}>
        {t('mkt_manifesto_body')}
      </AppText>
      <View style={{ flexDirection: 'row', gap: wide ? 40 : 24, marginTop: space.s6, flexWrap: 'wrap', justifyContent: 'center' }}>
        {stats.map(([value, labelKey]) => (
          <View key={labelKey} style={{ alignItems: 'center' }}>
            <AppText mono weight="500" style={{ color: fg, fontSize: 26 }}>{value}</AppText>
            <AppText variant="label" style={{ color: fgMuted, marginTop: 4 }}>{t(labelKey)}</AppText>
          </View>
        ))}
      </View>
    </View>
  );
}

function PricingAndDemo({ wide }: { wide: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      flexDirection: wide ? 'row' : 'column', gap: wide ? 64 : space.s6, alignItems: wide ? 'center' : 'stretch',
      justifyContent: 'center', paddingVertical: wide ? 88 : 48, paddingHorizontal: wide ? 64 : space.s5,
    }}>
      <View style={{
        width: wide ? 420 : '100%', borderWidth: 1, borderColor: colors.border, borderRadius: radius.xxl,
        padding: space.s6, backgroundColor: colors.canvas0,
      }}>
        <AppText variant="label" style={{ color: colors.primary500 }}>{t('mkt_pricing_kicker')}</AppText>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, marginTop: space.s3 }}>
          <AppText mono weight="500" style={{ fontSize: 40 }}>₹20</AppText>
          <AppText variant="bodyMd" tone="tertiary">{t('mkt_pricing_per')}</AppText>
        </View>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2, lineHeight: 22 }}>
          {t('mkt_pricing_body')}
        </AppText>
        <View style={{ gap: 8, marginTop: space.s4 }}>
          {PRICE_POINTS.map((pk) => (
            <View key={pk} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary500 }} />
              <AppText variant="bodySm" weight="500">{t(pk)}</AppText>
            </View>
          ))}
        </View>
      </View>
      <View style={{ maxWidth: wide ? 420 : undefined }}>
        <AppText tone="primary" style={{ fontFamily: 'Sora_800ExtraBold', fontSize: wide ? 28 : 21, lineHeight: wide ? 36 : 27 }}>
          {t('mkt_demo_title')}
        </AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s3, lineHeight: 22 }}>
          {t('mkt_demo_body')}
        </AppText>
        <Button title={t('mkt_request_demo')} size="lg" style={{ marginTop: space.s5, alignSelf: wide ? 'flex-start' : 'stretch' }} onPress={() => void Linking.openURL(DEMO_MAILTO)} />
      </View>
    </View>
  );
}

function Footer({ wide }: { wide: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      borderTopWidth: 0.5, borderTopColor: colors.borderSoft, paddingHorizontal: wide ? 64 : space.s5,
      paddingVertical: space.s6, flexDirection: wide ? 'row' : 'column', alignItems: wide ? 'center' : 'flex-start',
      gap: wide ? space.s6 : space.s3,
    }}>
      <BrandLockup markHeight={20} />
      <AppText variant="bodySm" tone="tertiary">{t('mkt_footer_copy')}</AppText>
      <View style={{ flexDirection: 'row', gap: space.s5, marginLeft: wide ? 'auto' : 0 }}>
        <Pressable onPress={() => void Linking.openURL(LEGAL_URL)}>
          <AppText variant="bodySm" weight="500" tone="secondary">{t('mkt_link_privacy')}</AppText>
        </Pressable>
        <Pressable onPress={() => void Linking.openURL(LEGAL_URL)}>
          <AppText variant="bodySm" weight="500" tone="secondary">{t('mkt_link_terms')}</AppText>
        </Pressable>
        <Pressable onPress={() => void Linking.openURL(DEMO_MAILTO)}>
          <AppText variant="bodySm" weight="500" tone="secondary">{t('mkt_link_contact')}</AppText>
        </Pressable>
      </View>
    </View>
  );
}

/* ---------------- web marketing page ---------------- */

function MarketingHome() {
  const { colors } = useTheme();
  const { user } = useSession();
  const { width } = useWindowDimensions();
  const wide = width >= WIDE;
  const scrollRef = React.useRef<ScrollView>(null);
  // featuresContainerY = position of the features block within the ScrollView's
  // content (direct-child onLayout, so this y is already scroll-relative).
  // sectionOffsets holds each feature's position *within* that container, so the
  // absolute scroll target for a given feature is containerY + itsOffset.
  const featuresContainerY = React.useRef(0);
  const sectionOffsets = React.useRef<Record<string, number>>({});
  const pricingY = React.useRef(0);

  const scrollToY = (y: number) => scrollRef.current?.scrollTo({ y: Math.max(y - 12, 0), animated: true });
  const scrollToFeature = (key: string) => scrollToY(featuresContainerY.current + (sectionOffsets.current[key] ?? 0));
  const scrollToPricing = () => scrollToY(pricingY.current);

  const navTo: NavTargets = {
    gate: () => scrollToFeature('gate'),
    community: () => scrollToFeature('community'),
    payments: () => scrollToFeature('payments'),
    pricing: scrollToPricing,
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <NavHeader wide={wide} signedIn={!!user} navTo={navTo} />
      <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false}>
        <Hero wide={wide} onSeeHowItWorks={() => scrollToY(featuresContainerY.current)} />
        <View
          onLayout={(e) => { featuresContainerY.current = e.nativeEvent.layout.y; }}
          style={{ paddingHorizontal: wide ? 64 : space.s5, paddingVertical: wide ? 88 : 40, gap: wide ? 72 : 48 }}
        >
          {FEATURES.map((f) => (
            <View key={f.key} onLayout={(e) => { sectionOffsets.current[f.key] = e.nativeEvent.layout.y; }}>
              <FeatureBlock feature={f} wide={wide} />
            </View>
          ))}
        </View>
        <Manifesto wide={wide} />
        <View onLayout={(e) => { pricingY.current = e.nativeEvent.layout.y; }}>
          <PricingAndDemo wide={wide} />
        </View>
        <Footer wide={wide} />
      </ScrollView>
    </View>
  );
}

/* ---------------- route ---------------- */

export default function Index() {
  if (Platform.OS !== 'web') return <NativeGate />;
  return <MarketingHome />;
}
